import { createStore, createEvent, createEffect, sample, attach } from 'effector';
import { USER_VARIABLE } from '@nx-frontend/shared/api';
import { clientSetUserVars } from '@nx-frontend/shared/api/webnotes/client';
import { $user } from '@nx-frontend/shared/store/user';
import { $userVars, setUserVarEvent } from '@nx-frontend/shared/store/user-vars';
import { setCookie } from '@nx-frontend/shared/utils';

export const DEFAULT_SIDEBAR_WIDTH = 272;
export interface SidebarState {
  width: number;
  fetchMenuTree?: boolean;
  isCollapsed: boolean;
  isResizing: boolean;
  scrollRestricted: boolean;
  isLoaded: boolean;
  isPortalSettingsCollapsed: boolean;
}

export const defaultSidebarState: SidebarState = {
  width: DEFAULT_SIDEBAR_WIDTH,
  fetchMenuTree: false,
  isCollapsed: false,
  isResizing: false,
  scrollRestricted: false,
  isLoaded: false,
  isPortalSettingsCollapsed: true,
};

export const $sidebar = createStore(defaultSidebarState);
export const $isSidebarCollapsed = $sidebar.map((state) => state.isCollapsed);
export const $isSidebarResizing = $sidebar.map((state) => state.isResizing);

export const setSidebarWidthEvent = createEvent<number>();
export const setSidebarFetchMenuTreeEvent = createEvent<boolean>();
export const resetSidebarFetchMenuTreeEvent = createEvent();
export const setIsSidebarCollapsedEvent = createEvent<boolean>();
export const setIsSidebarResizingEvent = createEvent<boolean>();
export const setIsSidebarScrollRestrictedEvent = createEvent<boolean>();
export const setSidebarIsLoadedEvent = createEvent<boolean>();

export const setIsSidebarCollapsedAndSaveFx = createEffect((value: boolean) => {
  setUserVarEvent({ key: USER_VARIABLE.SIDEBAR_COLLAPSED, value: value.toString() });
  return value;
});

export const setIsPortalSettingsCollapsedAndSaveFx = createEffect((value: boolean) => {
  setUserVarEvent({ key: USER_VARIABLE.SIDEBAR_PORTAL_SETTINGS_COLLAPSED, value: value.toString() });
  return value;
});

let DEBOUNCE_ID: number | NodeJS.Timeout = 0;

export const setSidebarCollapsedSavedStateFx = attach({
  source: [$userVars],
  effect: ([userVars]) => {
    setIsSidebarCollapsedEvent(
      userVars.find((vars) => vars.key === USER_VARIABLE.SIDEBAR_COLLAPSED)?.value === 'true' ?? false
    );
  },
});

export const setSidebarWidthFx = createEffect(async ({ userId, width }: { userId: number; width: number }) => {
  clearTimeout(DEBOUNCE_ID);
  DEBOUNCE_ID = setTimeout(() => {
    setCookie(USER_VARIABLE.SIDEBAR_WIDTH, width.toString());
    clientSetUserVars({ key: USER_VARIABLE.SIDEBAR_WIDTH, value: width, userId });
  }, 1000);
  return width;
});

sample({
  source: { user: $user },
  clock: setSidebarWidthEvent,
  fn: (store, width) => {
    return {
      userId: store.user.id,
      width,
    };
  },
  target: setSidebarWidthFx,
});

$sidebar.on(setSidebarWidthEvent, (state, width) => {
  return { ...state, width };
});

$sidebar.on(setSidebarFetchMenuTreeEvent, (state, fetchMenuTree) => {
  return { ...state, fetchMenuTree };
});

$sidebar.on(resetSidebarFetchMenuTreeEvent, (state) => {
  return { ...state, fetchMenuTree: false };
});

$sidebar.on(setIsSidebarCollapsedAndSaveFx.doneData, (state, value) => {
  return { ...state, isCollapsed: value };
});

$sidebar.on(setIsPortalSettingsCollapsedAndSaveFx.doneData, (state, value) => {
  return { ...state, isPortalSettingsCollapsed: value };
});

$sidebar.on(setIsSidebarCollapsedEvent, (state, value) => {
  return { ...state, isCollapsed: value };
});

$sidebar.on(setIsSidebarResizingEvent, (state, value) => {
  return { ...state, isResizing: value };
});

$sidebar.on(setIsSidebarScrollRestrictedEvent, (state, value) => {
  return { ...state, scrollRestricted: value };
});

$sidebar.on(setSidebarIsLoadedEvent, (state, value) => {
  return { ...state, isLoaded: value };
});
